import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Typography,
  Paper,
  CircularProgress,
} from '@mui/material';
import {
  AccessTime as TimeIcon,
  People as PeopleIcon,
  Group as GroupIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { BulkNotificationItem } from './BulkNotificationsTable';
import { getExpertBulkNotificationPreview } from '../../../services/notification.service';
import { ModalIndices } from '../../../common/i-doc/i-doc.registry.ts';

interface BulkNotificationDetailsModalProps {
  open: boolean;
  onClose: () => void;
  notification: BulkNotificationItem | null;
}

const LEVEL_LABELS: Record<string, string> = {
  info: 'Інформаційне',
  warning: 'Попередження',
  danger: 'Важливо / Терміново',
};

const LEVEL_COLORS: Record<string, 'info' | 'warning' | 'error'> = {
  info: 'info',
  warning: 'warning',
  danger: 'error',
};

export const BulkNotificationDetailsModal: React.FC<BulkNotificationDetailsModalProps> = ({
  open,
  onClose,
  notification,
}) => {
  const [recipients, setRecipients] = useState<{ id: number; name: string }[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && notification) {
      fetchRecipients();
    } else {
      setRecipients([]);
    }
  }, [open, notification]);

  const fetchRecipients = async () => {
    if (!notification) return;
    setLoading(true);
    try {
      const res = await getExpertBulkNotificationPreview({
        targetType: notification.targetType,
        groupId: notification.targetGroupId || undefined,
      });
      setRecipients(res);
    } catch (error) {
      console.error('Failed to fetch recipients for details:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!notification) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="sm"
      data-i-doc={ModalIndices.BULK_NOTIFICATION_DETAILS}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Деталі розсилки
        <Chip
          label={LEVEL_LABELS[notification.level]}
          color={LEVEL_COLORS[notification.level]}
          size="small"
        />
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <TimeIcon sx={{ fontSize: 16 }} /> Час відправки
            </Typography>
            <Typography variant="body1">
              {new Date(notification.createdAt).toLocaleString('uk-UA', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <PeopleIcon sx={{ fontSize: 16 }} /> Аудиторія
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {notification.targetType === 'all' ? (
                <Typography variant="body1">Всі клієнти</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {notification.groupNames?.map((name) => (
                    <Chip key={name} label={name} size="small" variant="outlined" icon={<GroupIcon />} />
                  ))}
                </Box>
              )}
            </Box>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <InfoIcon sx={{ fontSize: 16 }} /> Охоплення (на момент відправки)
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {notification.sentCount} клієнтів отримали
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              Список отримувачів (актуальний склад)
              {loading && <CircularProgress size={12} />}
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 0.5, 
              maxHeight: 120, 
              overflowY: 'auto',
              p: 1.5,
              bgcolor: 'action.hover',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider'
            }}>
              {recipients.length > 0 ? (
                recipients.map(r => (
                  <Chip key={r.id} label={r.name} size="small" variant="outlined" sx={{ bgcolor: 'background.paper' }} />
                ))
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {loading ? 'Завантаження...' : 'Список порожній або клієнти більше не підключені'}
                </Typography>
              )}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Текст повідомлення
            </Typography>
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                bgcolor: 'action.hover', 
                borderRadius: 2,
                borderLeft: 4,
                borderColor: `${LEVEL_COLORS[notification.level]}.main`
              }}
            >
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {notification.message}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined" fullWidth>
          Закрити
        </Button>
      </DialogActions>
    </Dialog>
  );
};
